<template>
  <div class="figure-item" :class="{ '-open': isOpen }">
    <div class="header" @click="toggleOpen()">
      <h3 v-if="'title' in item" class="title text-md">
        <span>{{ item.title }}</span>
      </h3>
      <button type="button" class="button">
        <img
          class="icon eye-closed"
          src="/icons/eye-closed.svg"
          alt="eye closed"
        />
        <img class="icon eye-open" src="/icons/eye-opened.svg" alt="eye open" />
      </button>
    </div>
    <div class="main">
      <StructuredText
        v-if="'content' in item && item.content"
        class="text-sm content"
        :data="item.content.value"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { StructuredText } from 'vue-datocms';
import type { ContentAccordionFragment } from '#gql';

defineProps<{
  item: ContentAccordionFragment['accordionItems'][number];
}>();

const isOpen = ref<boolean>(false);

function toggleOpen(open = !isOpen.value) {
  isOpen.value = open;
}
</script>

<style scoped lang="scss">
.figure-item {
  position: relative;
  background: var(--base-color-white-80);
  border-radius: var(--base-border-radius-small);
  padding: 1.5rem 2rem;
  width: 100%;
  margin-left: auto;
  cursor: pointer;
  :deep(.icon) {
    width: 100%;
  }

  :deep(.icon.eye-open) {
    opacity: 0;
  }

  &.-open {
    > .main {
      grid-template-rows: 1fr;
    }
    :deep(.icon.eye-closed) {
      opacity: 0;
    }
    :deep(.icon.eye-open) {
      opacity: 1;
    }
  }

  > .header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .button {
      background: none;
      border: none;
      cursor: pointer;
      height: 1.5rem;
      width: 1.5rem;
      position: relative;
      padding: 0;

      .icon {
        position: absolute;
        top: 4px;
        transition: opacity 200ms ease-in-out;
      }
    }
  }

  > .main {
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;
    transition: grid-template-rows 300ms ease-in-out;

    > .content {
      min-height: 0;
    }
  }
}

@media (--vs) {
  .figure-item {
    background: var(--base-color-white-80);

    > .header {
      gap: 1rem;
      grid-template-columns: auto 2rem;
      display: grid;
      .button {
        padding: 0;
      }
    }
  }
}
</style>
<style lang="scss">
.figure-item {
  .main {
    ul,
    p {
      margin-top: 1.5rem;
    }
    .content {
      ul {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-block: 1.25rem;

        li {
          display: flex;
          align-items: top;
        }

        li:before {
          margin-top: 2.5rem;
          content: ' ';
          height: 3px;
          border-radius: 8px;
          width: 1rem;
          min-width: 1rem;
          margin-right: 10px;
          background-color: var(--base-color-foreground);
        }
      }
    }
  }
}
</style>
